import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ArrowDown,
  ArrowUp,
  Braces,
  Info,
  ListPlus,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNodeStore } from '@/stores/node-store';
import { useOrgStore } from '@/stores/org-store';
import type { CustomNode, NodeStruct } from '@/types/node-types';
import type {
  ExpressionAst,
  JsonValue,
} from '@/pages/workflow/helper/functional-control-flow';
import { getParamValue } from '@/pages/workflow/helper/functional-control-flow';
import {
  MAX_CALCULATE_OUTPUTS,
  getCalculateConfig,
  readCalculateResult,
  serializeCalculateOutputs,
  validateCalculateConfig,
  type CalculateOutput,
} from '@/pages/workflow/helper/calculate';
import { fieldSuggestionsForSample } from '@/pages/workflow/helper/switch-condition-builder';
import { resolveSwitchInputSample } from '@/pages/workflow/helper/switch-branch-sync';
import {
  previewExpressions,
  type FormulaPreviewResult,
} from '@/services/workflow/expressions-preview';
import { cn } from '@/lib/utils';
import { JsonEditorField } from './json-editor-field';
import { CalculateResultCard } from './calculate-result-view';
import {
  FUNCTION_SUGGESTIONS,
  FormulaInput,
  fieldSuggestionsToFormula,
  type FormulaSuggestion,
} from './formula-input';

/**
 * Calculate panel: one row per output, `name = formula`. The formula box
 * suggests upstream fields, earlier outputs and function names; the value
 * column shows what the formula gives for the last upstream value, checked
 * by the backend parser (the only one), so the preview is what a run does.
 */

const PREVIEW_DEBOUNCE_MS = 350;

const EMPTY_ROWS: CalculateOutput[] = [{ name: '', formula: '', type: 'any' }];

function useDraft<T>(source: T): [T, (next: T) => void] {
  const [draft, setDraft] = useState(source);
  const [seen, setSeen] = useState(source);
  if (seen !== source) {
    setSeen(source);
    setDraft(source);
  }
  return [draft, setDraft];
}

const jsonTypeLabel = (value: unknown): string => {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'list';
  switch (typeof value) {
    case 'number':
      return 'number';
    case 'string':
      return 'text';
    case 'boolean':
      return 'boolean';
    case 'object':
      return 'object';
    default:
      return typeof value;
  }
};

const compactValue = (value: unknown, max = 60): string => {
  let text: string;
  if (value === undefined) text = '—';
  else if (typeof value === 'string') text = JSON.stringify(value);
  else text = JSON.stringify(value) ?? String(value);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

interface OutputRowProps {
  index: number;
  output: CalculateOutput;
  count: number;
  nameError: string | null;
  formulaError: string | null;
  preview: FormulaPreviewResult | undefined;
  previewPending: boolean;
  suggestions: FormulaSuggestion[];
  disabled: boolean;
  onChange: (next: CalculateOutput) => void;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
}

function OutputRow({
  index,
  output,
  count,
  nameError,
  formulaError,
  preview,
  previewPending,
  suggestions,
  disabled,
  onChange,
  onRemove,
  onMove,
}: OutputRowProps) {
  const [nameDraft, setNameDraft] = useDraft(output.name);

  const commitName = () => {
    const next = nameDraft.trim();
    if (next !== output.name) onChange({ ...output, name: next });
  };

  const problem = preview?.error ?? null;
  const resultType = preview?.evaluated ? jsonTypeLabel(preview.value) : null;
  const badge =
    resultType ?? (output.type && output.type !== 'any' ? output.type : null);

  return (
    <div
      className='rounded-md border border-border/60 p-2 space-y-1'
      data-testid={`calculate-output-${index}`}
    >
      {/* line 1: order, name, type, remove */}
      <div className='flex items-center gap-1.5'>
        <div className='flex flex-col items-center gap-0.5'>
          <button
            type='button'
            className='text-muted-foreground hover:text-foreground disabled:opacity-30'
            aria-label='Move output up'
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp className='h-3 w-3' />
          </button>
          <button
            type='button'
            className='text-muted-foreground hover:text-foreground disabled:opacity-30'
            aria-label='Move output down'
            disabled={disabled || index === count - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown className='h-3 w-3' />
          </button>
        </div>
        <Input
          value={nameDraft}
          placeholder='name'
          aria-label='Output name'
          data-testid='calculate-output-name'
          disabled={disabled}
          className={cn(
            'h-8 text-xs font-mono flex-1 min-w-0',
            nameError && 'border-destructive',
          )}
          onChange={(event) => setNameDraft(event.target.value)}
          onBlur={commitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitName();
            }
          }}
        />
        {badge && (
          <span
            className='rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground shrink-0'
            data-testid='calculate-output-type'
          >
            {badge}
          </span>
        )}
        <button
          type='button'
          className='text-muted-foreground hover:text-destructive disabled:opacity-30 shrink-0'
          aria-label='Remove output'
          data-testid='calculate-output-remove'
          disabled={disabled || count <= 1}
          onClick={onRemove}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </button>
      </div>
      {nameError && (
        <p className='text-xs text-destructive pl-5'>{nameError}</p>
      )}
      {/* line 2: = formula, full width */}
      <div className='flex items-start gap-1.5 pl-5'>
        <span className='text-xs text-muted-foreground select-none pt-2 font-mono'>
          =
        </span>
        <FormulaInput
          value={output.formula}
          suggestions={suggestions}
          disabled={disabled}
          placeholder='formula, e.g. round(score * 100) / 100'
          aria-label='Formula'
          data-testid='calculate-output-formula'
          problem={
            problem
              ? {
                  message: problem.message,
                  hint: problem.hint,
                  position: problem.position,
                }
              : formulaError
                ? { message: formulaError }
                : null
          }
          onCommit={(text) =>
            onChange({ ...output, formula: text, expression: null })
          }
        />
      </div>
      {!problem && preview?.evaluated && (
        <p
          className='text-xs text-muted-foreground font-mono truncate pl-8'
          data-testid='calculate-output-preview'
          title={JSON.stringify(preview.value)}
        >
          → {compactValue(preview.value)}
        </p>
      )}
      {!problem &&
        preview &&
        !preview.evaluated &&
        !previewPending &&
        preview.fields.length > 0 && (
          <p className='text-xs text-muted-foreground pl-8'>
            Reads {preview.fields.join(', ')}. Run the upstream node to see a
            value.
          </p>
        )}
    </div>
  );
}

interface CalculateOutputsEditorProps {
  nodeId: string;
  struct: NodeStruct;
  onChange: (outputs: Array<Record<string, unknown>>) => void;
  disabled?: boolean;
  info?: string;
}

export function CalculateOutputsEditor({
  nodeId,
  struct,
  onChange,
  disabled = false,
  info,
}: CalculateOutputsEditorProps) {
  const { node, nodes, edges } = useNodeStore(
    useShallow((state) => ({
      node: state.nodes.find((n) => n.id === nodeId) as CustomNode | undefined,
      nodes: state.nodes,
      edges: state.edges,
    })),
  );
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const [mode, setMode] = useState<'rows' | 'json'>('rows');

  const config = useMemo(() => getCalculateConfig(struct), [struct]);
  const errors = useMemo(() => validateCalculateConfig(config), [config]);
  const runSummary = useMemo(
    () => readCalculateResult(node?.data?.result),
    [node?.data?.result],
  );

  // The last value that reached the value input or, when nothing is
  // connected, the value typed on the node (a loop's seed).
  const valueConnected = useMemo(() => {
    const valuePortId = struct.params?.find((p) => p.field_id === 'value')
      ?.bindable_config?.input_port?.id;
    return edges.some(
      (edge) => edge.target === nodeId && edge.targetHandle === valuePortId,
    );
  }, [struct.params, edges, nodeId]);
  const typedValue = useMemo(() => {
    const raw = getParamValue(struct, 'value');
    if (raw === undefined || raw === null || raw === '') return undefined;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) as JsonValue;
      } catch {
        return raw as JsonValue;
      }
    }
    return raw as JsonValue;
  }, [struct]);
  const upstreamSample = useMemo(
    () => (node ? resolveSwitchInputSample(node, nodes, edges) : undefined),
    [node, nodes, edges],
  );
  const sample = valueConnected ? upstreamSample : typedValue;
  const fieldSuggestions = useMemo(
    () => fieldSuggestionsForSample(sample),
    [sample],
  );

  const upstreamName = useMemo(() => {
    const valuePortId = struct.params?.find((p) => p.field_id === 'value')
      ?.bindable_config?.input_port?.id;
    const incoming = edges.find(
      (edge) => edge.target === nodeId && edge.targetHandle === valuePortId,
    );
    const source = nodes.find((n) => n.id === incoming?.source);
    return (
      source?.data?.struct?.user_config_name ||
      source?.data?.struct?.display_name ||
      null
    );
  }, [struct.params, edges, nodes, nodeId]);

  // --- backend preview -------------------------------------------------------
  const [previews, setPreviews] = useState<FormulaPreviewResult[]>([]);
  const [previewPending, setPreviewPending] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState<string | null>(
    null,
  );
  const previewKey = useMemo(
    () =>
      JSON.stringify({
        outputs: config.outputs.map((o) => [o.name, o.formula]),
        sample: sample === undefined ? null : sample,
      }),
    [config.outputs, sample],
  );
  const latestRequest = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!orgId || config.outputs.length === 0) {
      setPreviews([]);
      return;
    }
    const outputs = config.outputs;
    const hasFormula = outputs.some((o) => o.formula.trim());
    if (!hasFormula) {
      setPreviews([]);
      return;
    }
    const requestId = (latestRequest.current += 1);
    setPreviewPending(true);
    const timer = window.setTimeout(() => {
      previewExpressions(orgId, {
        formulas: outputs.map((o) => ({
          name: o.name || undefined,
          formula: o.formula,
        })),
        sample: sample === undefined ? null : (sample as JsonValue),
        root: 'value',
        mode: 'calculate',
      })
        .then((response) => {
          if (requestId !== latestRequest.current) return;
          setPreviews(response.results);
          setPreviewUnavailable(null);
          // Keep the parsed tree next to the formula so readers (the Why
          // panel, agents) do not need to parse. Only when every formula
          // parsed and the text has not moved on.
          if (response.ok || response.results.every((r) => r.expression)) {
            const next = outputs.map((o, i) => {
              const result = response.results[i];
              if (!result || result.formula !== o.formula || !result.expression)
                return o;
              return { ...o, expression: result.expression as ExpressionAst };
            });
            const changed = next.some(
              (o, i) =>
                JSON.stringify(o.expression ?? null) !==
                JSON.stringify(outputs[i].expression ?? null),
            );
            if (changed && !disabled)
              onChangeRef.current(serializeCalculateOutputs(next));
          }
        })
        .catch((error: unknown) => {
          if (requestId !== latestRequest.current) return;
          setPreviewUnavailable(
            error instanceof Error ? error.message : 'Preview unavailable.',
          );
        })
        .finally(() => {
          if (requestId === latestRequest.current) setPreviewPending(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // previewKey captures outputs + sample; config/sample are derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, previewKey, disabled]);

  const commit = useCallback(
    (next: CalculateOutput[]) => onChange(serializeCalculateOutputs(next)),
    [onChange],
  );

  const update = (index: number, next: CalculateOutput) => {
    const outputs = config.outputs.slice();
    outputs[index] = next;
    commit(outputs);
  };
  const remove = (index: number) =>
    commit(config.outputs.filter((_, i) => i !== index));
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= config.outputs.length) return;
    const outputs = config.outputs.slice();
    [outputs[index], outputs[target]] = [outputs[target], outputs[index]];
    commit(outputs);
  };
  const add = () => {
    const names = new Set(config.outputs.map((o) => o.name));
    let n = config.outputs.length + 1;
    while (names.has(`output_${n}`)) n += 1;
    commit([
      ...config.outputs,
      { name: `output_${n}`, formula: '', type: 'any' },
    ]);
  };

  const errorAt = (index: number, field: 'name' | 'formula'): string | null =>
    errors.find((e) => e.index === index && e.field === field)?.message ?? null;
  const generalErrors = errors.filter((e) => e.index === null);

  const formulaFieldSuggestions = useMemo(
    () => fieldSuggestionsToFormula(fieldSuggestions),
    [fieldSuggestions],
  );
  const suggestionsFor = (index: number): FormulaSuggestion[] => [
    ...formulaFieldSuggestions,
    ...config.outputs
      .slice(0, index)
      .filter((o) => o.name)
      .map((o) => ({ value: o.name, kind: 'output' as const })),
    ...FUNCTION_SUGGESTIONS,
  ];

  return (
    <div className='w-full mb-4 px-3' data-testid='calculate-outputs-editor'>
      <div className='flex items-center justify-between gap-2 mb-1 min-h-[36px]'>
        <div className='flex items-center gap-1'>
          <Label className='text-sm label-required'>Outputs</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className='h-4 w-4 text-muted-foreground cursor-help' />
            </TooltipTrigger>
            <TooltipContent>
              <p className='max-w-xs'>
                {info ||
                  'Each output is a name and a formula over the incoming value. Later outputs can use earlier ones by name. Arithmetic (+ - * / % ^), text (~ to join), comparisons, and/or/not, and functions like round(), len(), map().'}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-7 text-xs'
          data-testid='calculate-mode-toggle'
          onClick={() => setMode(mode === 'rows' ? 'json' : 'rows')}
        >
          {mode === 'rows' ? (
            <>
              <Braces className='h-3 w-3 mr-1' /> Edit as JSON
            </>
          ) : (
            <>
              <ListPlus className='h-3 w-3 mr-1' /> Edit as rows
            </>
          )}
        </Button>
      </div>

      {runSummary && (
        <div className='mb-3' data-testid='calculate-run-result'>
          <CalculateResultCard summary={runSummary} />
        </div>
      )}

      <p
        className='text-xs text-muted-foreground mb-2'
        data-testid='calculate-preview-source'
      >
        {sample !== undefined
          ? valueConnected
            ? `Previewing with the last value from ${upstreamName ?? 'the connected node'}.`
            : 'Previewing with the value typed above.'
          : upstreamName
            ? `Connected to ${upstreamName}. Run it once to preview values here.`
            : 'Connect a node to the value input or type a value above; its fields appear as suggestions.'}
        {previewUnavailable && (
          <span className='block text-amber-600'>
            Live check unavailable: {previewUnavailable}
          </span>
        )}
      </p>

      {mode === 'json' ? (
        <JsonEditorField
          value={serializeCalculateOutputs(config.outputs)}
          expectedShape='array'
          aria-label='Outputs JSON'
          data-testid='calculate-outputs-json'
          disabled={disabled}
          rows={10}
          validate={(value) => {
            if (!Array.isArray(value)) return 'Outputs must be a list.';
            for (const [i, entry] of value.entries()) {
              if (!entry || typeof entry !== 'object' || Array.isArray(entry))
                return `outputs[${i}] must be an object with name and formula.`;
              const row = entry as Record<string, unknown>;
              if (typeof row.name !== 'string')
                return `outputs[${i}].name must be text.`;
              if (
                typeof row.formula !== 'string' &&
                (row.expression === undefined || row.expression === null)
              )
                return `outputs[${i}] needs a formula (text) or an expression (tree).`;
            }
            return null;
          }}
          onCommit={(value) =>
            onChange(value as Array<Record<string, unknown>>)
          }
        />
      ) : (
        <div className='space-y-2'>
          {(config.outputs.length === 0 ? EMPTY_ROWS : config.outputs).map(
            (output, index) => (
              <OutputRow
                key={index}
                index={index}
                output={output}
                count={Math.max(config.outputs.length, 1)}
                nameError={errorAt(index, 'name')}
                formulaError={errorAt(index, 'formula')}
                preview={previews[index]}
                previewPending={previewPending}
                suggestions={suggestionsFor(index)}
                disabled={disabled}
                onChange={(next) =>
                  config.outputs.length === 0
                    ? commit([next])
                    : update(index, next)
                }
                onRemove={() => remove(index)}
                onMove={(delta) => move(index, delta)}
              />
            ),
          )}
          <div className='flex items-center justify-between'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-7 text-xs'
              data-testid='calculate-add-output'
              disabled={
                disabled || config.outputs.length >= MAX_CALCULATE_OUTPUTS
              }
              onClick={add}
            >
              <Plus className='h-3 w-3 mr-1' /> Add output
            </Button>
            <span className='text-[11px] text-muted-foreground'>
              {config.outputs.length}/{MAX_CALCULATE_OUTPUTS}
            </span>
          </div>
          {generalErrors.map((error) => (
            <p key={error.message} className='text-xs text-destructive'>
              {error.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
